<template>
  <div class="login-page">
    <div class="login-card">
      <img
        src="../assets/brand_logo_black.png"
        class="login-logo"
        alt="Smartz"
      />
      <p class="login-subtitle">
        Sign in with your Microsoft account to continue
      </p>
      <div v-if="errorMsg" class="login-error">
        {{ errorMsg }}
      </div>
      <button class="login-btn" :disabled="loading" @click="signIn">
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm"
          role="status"
        ></span>
        {{ $t('text.sign_in') | capitalize }}
      </button>
    </div>
  </div>
</template>

<script>
import { encode as base64encode } from 'base64-arraybuffer'
import randomstring from 'randomstring'
import NProgress from 'nprogress'
import { azureAD } from '@/const'

export default {
  name: 'login',
  data() {
    return {
      loading: false,
      errorMsg: null
    }
  },
  mounted() {
    if (this.$route.query.code) {
      this.loading = true
      NProgress.start()
      this.$store
        .dispatch('user/getAzureToken', this.$route.query.code)
        .then(() => this.$store.dispatch('user/verifyUser'))
        .then(() => {
          NProgress.done()
          this.loading = false
          this.$router.push('/')
        })
        .catch(() => {
          NProgress.done()
          this.loading = false
          this.errorMsg = 'Sign in failed. Please try again.'
        })
    } else if (this.$route.query.error_description) {
      this.errorMsg = this.$route.query.error_description
    }
  },
  methods: {
    async generateCodeChallenge(codeVerifier) {
      const encoder = new TextEncoder()
      const data = encoder.encode(codeVerifier)
      const digest = await window.crypto.subtle.digest('SHA-256', data)
      return base64encode(digest)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '')
    },
    async signIn() {
      this.loading = true
      NProgress.start()
      const codeVerifier = randomstring.generate(128)
      localStorage.setItem('codeVerifier', codeVerifier)
      const challenge = await this.generateCodeChallenge(codeVerifier)
      window.open(
        `https://login.microsoftonline.com/${azureAD.tenantId}/oauth2/v2.0/authorize` +
          `?client_id=${azureAD.clientId}` +
          `&response_type=code` +
          `&redirect_uri=${encodeURIComponent(azureAD.redirectUri)}` +
          `&response_mode=query` +
          `&scope=${encodeURIComponent(azureAD.scope)}` +
          `&code_challenge=${challenge}` +
          `&code_challenge_method=S256`,
        '_self'
      )
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1e2433;
  background-image: radial-gradient(
      ellipse at 20% 50%,
      rgba(59, 130, 246, 0.12) 0%,
      transparent 60%
    ),
    radial-gradient(
      ellipse at 80% 20%,
      rgba(59, 130, 246, 0.08) 0%,
      transparent 50%
    );
}

.login-card {
  background: #fff;
  border-radius: 16px;
  padding: 52px 44px 44px;
  width: 100%;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
}

.login-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}

.login-logo {
  width: 180px;
  margin-bottom: 32px;
}

.login-subtitle {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 36px;
}

.login-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 20px;
  text-align: left;
}

.login-btn {
  width: 100%;
  padding: 13px;
  background: #3b82f6;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
}

.login-btn:hover:not(:disabled) {
  background: #2563eb;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.5);
}

.login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
